import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { cn } from '../../lib/utils';
import {
  LayoutDashboard,
  Users,
  UserPlus,
  Scale,
  Clock,
  BarChart3,
  Settings,
  LogOut,
  ChevronLeft,
  ChevronRight,
  Flame,
  HardHat,
  Timer,
  CalendarClock,
  BookLock,
} from 'lucide-react';
import { useState } from 'react';
import { Button } from '../ui/button';

const navItems = [
  { icon: LayoutDashboard, label: 'Dashboard', path: '/', roles: ['admin', 'hr_manager', 'line_manager'] },
  { icon: Users, label: 'Employees', path: '/employees', roles: ['admin', 'hr_manager', 'line_manager'] },
  { icon: UserPlus, label: 'Add Employee', path: '/employees/new', roles: ['admin', 'hr_manager'] },
  { icon: Timer, label: 'Time & Attendance', path: '/attendance', roles: ['admin', 'hr_manager'] },
  { icon: HardHat, label: 'PPE Management', path: '/ppe', roles: ['admin', 'hr_manager', 'line_manager'] },
  { icon: Scale, label: 'Disciplinary', path: '/disciplinary', roles: ['admin', 'hr_manager', 'line_manager'] },
  { icon: Clock, label: 'Leave & Absence', path: '/leave', roles: ['admin', 'hr_manager', 'line_manager'] },
  { icon: BookLock, label: 'Employee Notebook', path: '/notebook', master: true },
  { icon: BarChart3, label: 'Reports', path: '/reports', roles: ['admin', 'hr_manager'] },
  { icon: Settings, label: 'Settings', path: '/settings', roles: ['admin'] },
];

function useFilteredNav() {
  const { user } = useAuth();
  return navItems.filter(item =>
    item.master ? !!user?.is_master : item.roles.includes(user?.role)
  );
}

/**
 * Shared inner navigation used by both the desktop sidebar and the mobile drawer.
 * `collapsed` only applies to the desktop rail. `onNavigate` lets the mobile
 * drawer close itself when a link is tapped.
 */
export function SidebarInner({ collapsed = false, onNavigate }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const filteredNavItems = useFilteredNav();

  const handleLogout = () => {
    onNavigate?.();
    logout();
    navigate('/login');
  };

  return (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className="p-6 border-b border-white/5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-500 to-red-600 flex items-center justify-center shadow-lg shadow-orange-500/20">
            <Flame className="w-5 h-5 text-white" />
          </div>
          {!collapsed && (
            <div>
              <h1 className="font-bold text-lg text-foreground"><span className="text-orange-500">Hot</span>forge</h1>
              <p className="text-xs text-muted-foreground">People Management</p>
            </div>
          )}
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
        {filteredNavItems.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            end={item.path === '/'}
            onClick={() => onNavigate?.()}
            data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
            className={({ isActive }) => cn(
              "sidebar-item",
              isActive && "active"
            )}
          >
            <item.icon className="w-5 h-5 flex-shrink-0" />
            {!collapsed && <span>{item.label}</span>}
          </NavLink>
        ))}
      </nav>

      {/* User section */}
      <div className="p-4 border-t border-white/5">
        {!collapsed && (
          <div className="mb-4 p-3 rounded-lg bg-secondary/30">
            <p className="font-medium text-sm text-foreground truncate">{user?.full_name}</p>
            <p className="text-xs text-muted-foreground capitalize">{user?.role?.replace('_', ' ')}</p>
          </div>
        )}

        <Button
          variant="ghost"
          size="sm"
          onClick={handleLogout}
          data-testid="logout-btn"
          className={cn(
            "w-full justify-start text-muted-foreground hover:text-red-400",
            collapsed && "justify-center"
          )}
        >
          <LogOut className="w-5 h-5" />
          {!collapsed && <span className="ml-2">Logout</span>}
        </Button>
      </div>
    </div>
  );
}

/** Desktop fixed sidebar rail (hidden on < md screens). */
export function Sidebar() {
  const [collapsed, setCollapsed] = useState(false);

  return (
    <aside className={cn(
      "hidden md:flex fixed left-0 top-0 h-screen bg-[#020408] border-r border-white/5 flex-col transition-all duration-300 z-50",
      collapsed ? "w-20" : "w-64"
    )}>
      <SidebarInner collapsed={collapsed} />

      {/* Collapse toggle */}
      <button
        onClick={() => setCollapsed(!collapsed)}
        className="absolute -right-3 top-20 w-6 h-6 rounded-full bg-secondary border border-border flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors"
        data-testid="sidebar-toggle"
      >
        {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
      </button>
    </aside>
  );
}
